import { Check, ChevronDownIcon } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Highlight } from "@/components/highlight";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import { matchesSearch } from "@/lib/search";
import { cn } from "@/lib/utils";

import { statusLabel } from "./utils";

/** What the picker lists of an item. */
interface PickableItem {
  id: string;
  title: string;
  status: string;
}

interface ItemPickerProps {
  id?: string;
  /** The items it offers (`waitCandidates`), in the roadmap's order. */
  items: readonly PickableItem[];
  /** The id of the item chosen now; null for none. */
  value: string | null;
  /** Called with the item picked; the list then closes. */
  onChange: (id: string) => void;
  /** What the button says while nothing is chosen, and the list's name: "Item kiezen". */
  label: string;
  className?: string;
}

/**
 * One of the roadmap's items to choose, searchable, in the dashboard's theme,
 * like `<PersonPicker>`: a button like `<SelectTrigger>` that shows the item
 * chosen opens a search box over the list, each item's title with its status
 * muted beside it. Typing filters on the title. Keyboard: the arrows move,
 * Enter picks, Escape closes.
 */
export function ItemPicker({ id, items, value, onChange, label, className }: ItemPickerProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const options = items.filter((item) => matchesSearch([item.title], query));
  const current = options.length > 0 ? Math.min(active, options.length - 1) : null;
  const optionId = (index: number) => `${listId}-${index}`;
  const chosen = items.find((item) => item.id === value) ?? null;

  // Keep the option the arrows reached in view.
  useEffect(() => {
    if (open) {
      document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [active, open, listId]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setActive(0);
    }
  };

  const pick = (picked: string) => {
    onChange(picked);
    changeOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && options.length > 0) {
      event.preventDefault();
      setActive((index) => (index + 1) % options.length);
    } else if (event.key === "ArrowUp" && options.length > 0) {
      event.preventDefault();
      setActive((index) => (index - 1 + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = current === null ? undefined : options[current];
      if (item) {
        pick(item.id);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          data-size="sm"
          aria-label={chosen ? `${label}, ${chosen.title}` : label}
          className={cn(SELECT_TRIGGER_CLASS, className)}
        >
          <span className={cn("truncate", !chosen && "text-muted")}>{chosen?.title ?? label}</span>
          <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="border-b p-2">
          <Input
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current === null ? undefined : optionId(current)}
            aria-label={t("roadmap.form.itemSearch")}
            placeholder={t("roadmap.form.itemSearch")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-9 pointer-coarse:h-10"
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="scrollbar-themed max-h-64 overflow-y-auto p-1"
        >
          {options.map((item, index) => {
            const on = item.id === value;
            return (
              <li
                key={item.id}
                id={optionId(index)}
                role="option"
                aria-selected={on}
                // Keep the focus in the search box until the pick closes the list.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(item.id)}
                onMouseMove={() => setActive(index)}
                className={cn(
                  "flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5",
                  index === current && "bg-accent-light",
                )}
              >
                <Check
                  aria-hidden="true"
                  className={cn("text-primary size-4 shrink-0", !on && "invisible")}
                />
                <span className="text-foreground min-w-0 flex-1 truncate font-medium">
                  <Highlight text={item.title} query={query} />
                </span>
                <span className="text-muted shrink-0 text-xs">{statusLabel(item.status)}</span>
              </li>
            );
          })}
          {options.length === 0 && (
            <li role="presentation" className="text-muted px-2 py-1.5 text-sm">
              {t("roadmap.form.noItems")}
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
